import request from 'tools/request'

export const SET_MONITORING_ALERT_LIST = 'SET_MONITORING_ALERT_LIST'
export const SET_MONITORING_CONFIG = 'SET_MONITORING_CONFIG'
export const SET_MONITORING_CONFIGURATIONS = 'SET_MONITORING_CONFIGURATIONS';
export const SET_MONITORING_SUMMARY = 'SET_MONITORING_SUMMARY';

export const dispatchMonitoringAlertList = (data, headers) => ({
    type: SET_MONITORING_ALERT_LIST,
    data,
    headers
})

export const dispatchMonitoringSummary = (data) => ({
    type: SET_MONITORING_SUMMARY,
    data
})

// Portfolio counts for the header tiles. error404Redirect=false so a not-yet-deployed summary endpoint
// fails gracefully instead of redirecting the whole user to /404 (summary is an optional enhancement).
export const getMonitoringSummary = () => async (dispatch) => {
    const res = await request('applications/monitoring/summary', 'GET', null, false, {}, null, false)
    dispatch(dispatchMonitoringSummary(res))
    return res
}

// Acknowledge one alert (optional note). Returns the updated alert.
export const acknowledgeMonitoringAlert = (uuid, note) => async () => {
    return request(`applications/monitoring/alerts/${uuid}/acknowledge`, 'PATCH', note ? { note } : {}, false)
}

// Bulk acknowledge (e.g. "Acknowledge all" for a business).
export const acknowledgeMonitoringAlerts = (uuids, note) => async () => {
    return request('applications/monitoring/alerts/acknowledge', 'PATCH', { uuids, note: note || null }, false)
}

// Add an attributed note to an alert without acknowledging it (stays in review). Returns the updated alert.
export const addMonitoringAlertNote = (uuid, note) => async () => {
    return request(`applications/monitoring/alerts/${uuid}/notes`, 'POST', { note }, false)
}

// Hide a note (soft-delete; only the author may hide their own). Returns the updated alert.
export const hideMonitoringAlertNote = (uuid, noteId) => async () => {
    return request(`applications/monitoring/alerts/${uuid}/notes/${noteId}/hide`, 'PATCH', {}, false)
}

// The client's per-signal severity configuration (effective + defaults).
// error404Redirect=false so a not-yet-deployed endpoint fails gracefully instead of routing to /404.
export const getMonitoringSeverityConfig = () => async () => {
    return request('applications/monitoring/severity-config', 'GET', null, false, {}, null, false)
}

// Save the client's severity overrides (signal -> severity) and per-signal "worsening only" direction
// preferences (signal -> bool, directional signals only). A severity equal to the default with
// worseningOnly=false clears the row.
export const updateMonitoringSeverityConfig = (overrides, worseningOnly) => async () => {
    return request('applications/monitoring/severity-config', 'PUT', { overrides, worseningOnly }, false)
}

// Catalog of extra report fields a client can choose to monitor ([{reportType, key, label}]).
export const getMonitoringFieldCatalog = () => async () => {
    return request('applications/monitoring/field-catalog', 'GET', null, false, {}, null, false)
}

// The client's chosen watched fields ([{reportType, fieldKey, severity}]).
export const getMonitoringWatchedFields = () => async () => {
    return request('applications/monitoring/watched-fields', 'GET', null, false, {}, null, false)
}

// Replace the client's watched fields (only entries with a real severity are watched).
export const updateMonitoringWatchedFields = (fields) => async () => {
    return request('applications/monitoring/watched-fields', 'PUT', { fields }, false)
}

// Distinct monitored businesses (with alerts) for the ledger's business filter dropdown.
// error404Redirect=false so a not-yet-deployed endpoint fails gracefully instead of routing to /404.
export const getMonitoringBusinesses = () => async () => {
    return request('applications/monitoring/businesses', 'GET', null, false, {}, null, false)
}

// DEV-ONLY: whether the monitoring scenario builder is available in this environment.
export const getMonitoringDevEnabled = () => async () => {
    return request('applications/monitoring/dev/enabled', 'GET', null, false, {}, null, false)
}

// DEV-ONLY: fabricate one test alert (source/severity/ack state) for a named business.
export const createMonitoringTestAlert = (body) => async () => {
    return request('applications/monitoring/dev/test-alert', 'POST', body, false, {}, null, false)
}

// DEV/LOCAL-ONLY: run every enabled report for one application immediately (ignoring schedule); returns the
// alerts created. In demo mode this fires against the seeded demo data so real alerts appear.
export const runMonitoringNow = (appId) => async () => {
    return request(`applications/${appId}/monitoring/dev/run-now`, 'POST', null, false, {}, null, false)
}

// Cost preview for enabling monitoring on many businesses at once.
export const previewBulkMonitoring = (body) => async () => {
    return request('applications/monitoring/bulk/preview', 'POST', body, false)
}

// Apply a monitoring schedule + report selection across many businesses at once.
export const applyBulkMonitoring = (body) => async () => {
    return request('applications/monitoring/bulk/apply', 'POST', body, false)
}

// --- Monitoring notification recipients / audit (client-admin managed) ---

// The client's managed recipient list ([{id, email, enabled}]). Sent the digest in addition to each
// config's creator. error404Redirect=false so a not-yet-deployed endpoint fails gracefully.
export const getMonitoringRecipients = () => async () => {
    return request('monitoring-notifications/recipients', 'GET', null, false, {}, null, false)
}

// Replace the client's managed recipient list. Admin-only server-side. Returns the saved list.
export const updateMonitoringRecipients = (recipients) => async () => {
    return request('monitoring-notifications/recipients', 'PUT', { recipients }, false)
}

// The client's user emails, for the recipient picker (so recipients are chosen from real users rather than
// free-typed addresses). Accessible to the recipient admin (client-scoped, no extra privilege).
// error404Redirect=false so a not-yet-deployed endpoint fails gracefully instead of routing to /404.
export const getMonitoringRecipientCandidates = () => async () => {
    return request('monitoring-notifications/candidate-users', 'GET', null, false, {}, null, false)
}

// The notification send audit trail ([{recipient, subject, business, sentAt}], newest first).
export const getMonitoringNotificationAudit = () => async () => {
    return request('monitoring-notifications/audit', 'GET', null, false, {}, null, false)
}

// Email a fresh digest of the client's current open (unacknowledged) alerts to all recipients.
// Admin-only server-side. Returns { sent }.
export const sendOpenAlertsNotification = () => async () => {
    return request('monitoring-notifications/send-open-alerts', 'POST', null, false)
}

export const dispatchMonitoringConfig = (data) => ({
    type: SET_MONITORING_CONFIG,
    data
});

export const dispatchMonitoringConfigurations = (data, headers) => ({
    type: SET_MONITORING_CONFIGURATIONS,
    data,
    headers
});

export const getMonitoringAlerts = (pagination = {}, filters = {}, demoMode = false) => async (dispatch, getState) => {
    const res = await request(
        'applications/monitoring/alerts',
        'GET',
        null,
        true,
        {
            'x-pagination-num': pagination.page || 0,
            'x-pagination-limit': pagination.limit || 10,
            'x-pagination-sort': `${pagination?.orderBy?.field || 'createdAt'},${pagination?.orderBy?.order || 'desc'}`,
            'x-demo-mode': demoMode
        },
        filters
    )
    dispatch(dispatchMonitoringAlertList(res.response, res.headers))
    return res
}

// New action for fetching monitoring config
export const getMonitoringConfig = (roxAppId) => async (dispatch) => {
    const res = await request(
        `applications/${roxAppId}/monitoring/config`,
        'GET',
        null,
        true
    )
    dispatch(dispatchMonitoringConfig(res.response))
    return res
}

// Turn the current user's own "Notify me" on or off for one monitor. Returns { notifyMe }.
export const setMonitoringNotifyMe = (roxAppId, notifyMe) => async () => {
    return request(`applications/${roxAppId}/monitoring/notify-me`, 'PUT', { notifyMe }, false)
}

// Whether the current user paused all their monitoring alerts ({ paused }), and pausing/resuming them.
export const getMonitoringAlertsPaused = () => async () => {
    return request('monitoring-notifications/me', 'GET', null, false)
}

export const setMonitoringAlertsPaused = (paused) => async () => {
    return request('monitoring-notifications/me', 'PUT', { paused }, false)
}

export const updateMonitoringConfig = (roxAppId, data) => async (dispatch) => {
    const res = await request(
        `applications/${roxAppId}/monitoring/config`,
        'PATCH',
        data,
        true
    )
    // Optionally, you can refresh the config after update
    dispatch(dispatchMonitoringConfig(res.response))
    return res
}

export const getMonitoringConfigurationList = (pagination = {}, filters = {}, demoMode = false) => async (dispatch) => {
    const res = await request(
        `applications/monitoring/configurations`,
        'GET',
        null,
        true,
        {
            'x-pagination-num': pagination.page || 0,
            'x-pagination-limit': pagination.limit || 10,
            'x-pagination-sort': `${pagination?.orderBy?.field || 'createdAt'},${pagination?.orderBy?.order || 'desc'}`,
            'x-demo-mode': demoMode
        },
        filters
    );
    dispatch(dispatchMonitoringConfigurations(res.response, res.headers));
    return res;
};